<!-- JAVASCRIPT START -->
<script>
  import { createEventDispatcher } from 'svelte';
  import { fade } from 'svelte/transition';

  import Button from '../shared/Button.svelte';
  import FooterPrice from '../shared/FooterPrice.svelte';
  import Modal from '../shared/Modal.svelte';
  import ModalContent from '../shared/ModalContent.svelte';
  import SidebarTitle from '../shared/SidebarTitle.svelte';
  import Toggle from '../shared/Toggle.svelte';

  const dispatch = createEventDispatcher();
  
  ////////////////
  // Variables
  ////////////////
  export let showPricing = true;
  export let priceTally;
  export let previousStep;
  export let baseSystem;
  let selectedCheckbox = [];
  let selectedRadio = null;
  let isRadio;
  let optionsSubtotal = priceTally.usd;

  ////////////////
  // Data
  ////////////////
  const data = {
    "title" : "Select Additional Options",
    "required" : false,
    "multiselect" : true,
    "options" : [
        {
            "id" : 3001,
            "name" : "High Density Storage",
            "price" : 3900,
            "showPriceInButton" : true,
            "description" : "Achieve highest possible storage capacity by using high-density storage racks. Price is dependent on labware type and might be lower than stated.",
            "baseSystemExclusion" : "Plate Store"
        },
        {
            "id" : 3002,
            "name" : "Rack Hand-off",
            "price" : 14100,
            "showPriceInButton" : true,
            "description" : "<strong>Includes integration support.</strong> Transfers racks or plates to an external robotic system such as a Liquid Handler. Suitable for storage temperatures of 4°C or higher. Includes full INSTINCT S API and two days of remote support. For hand-off at -20°C storage temperatures, please <a href=\"https://www.hamiltoncompany.com/automated-sample-management/support\" class=\"link\" target=”_blank”>contact Hamilton Storage.</a>"
        },
        {
            "id" : 3003,
            "name" : "Monitoring Package",
            "price" : 4000,
            "showPriceInButton" : true,
            "description" : "Camera monitoring and recording system with two cameras to record the robotic movements within the store."
        },
        {
            "id" : 3004,
            "name" : "UPS",
            "price" : 2500,
            "showPriceInButton" : true,
            "description" : "UPS to back up power for system robotics and controlling computer. Before running out of power, the system will move into a safe state and shut down the computer safely."
        },
        {
            "id" : 3005,
            "name" : "SQL Server Full Licence",
            "price" : 3300,
            "showPriceInButton" : true,
            "description" : "Full SQL License for extensive storage of logs and traces."
        },
        {
            "id" : 3006,
            "name" : "Full INSTINCT S API",
            "price" : 8100,
            "showPriceInButton" : true,
            "description" : "<strong>Includes integration support.</strong> Advanced API  to control extended functions from a LIMS system. This item includes 2 days of integration support."
        }
    ]
  };


  function handleSubmitClick() {
    //console.log(fetchCartItems());
    dispatch('nextStep', fetchCartItems());
  }

  function fetchCartItems() {
    let cartItems = [];

    //add madatory radio item
    if(selectedRadio){
      cartItems.push(data.options[selectedRadio.index]);
    }

    //add checkboxes
    if(selectedCheckbox.length > 0){
      for(let i=0; i <= selectedCheckbox.length - 1; i++) {
        cartItems.push(data.options[selectedCheckbox[i]]);
      }
    }

    return cartItems;
  }

  function fetchSubtotal(checkboxIndex) {
    let prices = [priceTally.unformatted];

    //console.log(checkboxIndex);

    //get checkbox prices
    if(checkboxIndex.length > 0){
      for(let i=0; i <= checkboxIndex.length - 1; i++) {
        prices.push(data.options[checkboxIndex[i]].price);
      }
    }

    //sum all prices
    let tally = prices.reduce(function(a, b){
        return a + b;
    }, 0);

    return formatPrice(tally);

  }
  
  function formatPrice(num){
		let formatter = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 0
		});

		return formatter.format(num);
	}
</script>
<!-- JAVASCRIPT END -->



<!-- STYLES START -->
<style>
  
  @media (min-width: 1024px) {
    .bg {
      background-image: url(/img/additionalOptions--hero@3x.jpg);
    }
  }

</style>
<!-- STYLES END -->



<!-- HTML START -->

<!-- desktop images -->
<link rel="preload" href="img/purchaseOptions--hero@3x.jpg" as="image" media="(min-width: 1024px)">

<!-- mobile images -->
<link rel="preload" href="img/purchaseOptions--hero.jpg" as="image" media="(max-width: 1023px)">


<div class="relative lg:h-full lg:overflow-y-hidden">
  <div class="bg absolute top-0 left-0"></div>
	<div class="relative z-10 lg:h-full lg:overflow-y-scroll">
    <div class="flex flex-col lg:flex-row flex-no-wrap w-100 text-lg">
      <div class="flex-column flex-1"></div>
      <div class="flex-column flex-1">
        <div class="bg-white lg:my-12 lg:mb-12 lg:mx-10 lg:rounded lg:shadow-2xl">

          <div class="relative lg:hidden">
            <img class="w-full md:w-1/2 md:m-auto md:mt-10" src="/img/additionalOptions--hero.jpg" alt="Verso Q20 rendering">
            <button class="blueButton absolute bottom-0 inset-x-0 rounded-full text-white text-base px-6 py-2 w-8/12 mx-auto mb-4" on:click={() => dispatch('handleStepBack', {stepName: 1})}>
              <i class="fal fa-long-arrow-left"></i> Back to {previousStep}
            </button>
          </div>

          <SidebarTitle title={data.title} />
          
          <main class="flex-grow">

            <p class="text-gray-600 text-xl text-center p-10"><strong>Basic API (Included)</strong> - Application Programming Interface allows user to create pick jobs and use other basic functions from an external software.</p>

            <hr>

            {#each data.options as option, i}
              {#if option.baseSystemExclusion !== baseSystem}
                <div class="p-10">
                  <Toggle type='checkbox' bind:group={selectedCheckbox} value={i} let:checked={checked} buttonLabel={option.name} buttonPrice={showPricing ? option.showPriceInButton : false} price={option.price} />
                  <p class="text-gray-600 text-lg text-center mt-6">{@html option.description}</p>
                </div>
                <hr>
              {/if}
            {/each}

          </main>

          <footer class="nav flex flex-shrink lg:rounded-b px-10 py-6">
              {#if showPricing}
                <FooterPrice subtotal={fetchSubtotal(selectedCheckbox)} />
              {/if}

              <div class="flex-1">
                <Button disabled={data.required && !selectedRadio} title="Next Step" id="submitButton" on:click={handleSubmitClick} />
              </div>
          </footer>
        </div>
      </div>
    </div>
  </div>
</div>
<!-- HTML END -->